<!-- Localized -->
<template>
  <div class="w-full">
    <div v-if="isLoaderShown" class="flex justify-center items-center w-full h-full">
      <Loader />
    </div>

    <template v-else>
      <UISection class="mb-4">
        <template #title>
          <span class="text-white-text">{{ $t('components.workflow.details') }}</span>
        </template>

        <div class="flex gap-4 px-4">
          <template v-for="element in detailsElements">
            <FormulateInput
              v-model="details.model[element.key]"
              :key="element.key"
              :validation="element.validation"
              error-behavior="live"
              class="flex-1"
              :label="element.label"
              :placeholder="element.placeholder"
              type="text"
              :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
              :disabled="isLoading || isViewMode"
              @validation="($event) => validateTriggerField($event, 'details', element.key)"
            />
          </template>
        </div>
      </UISection>

      <UISection class="mb-4">
        <template #title>
          <span class="text-white-text">{{ $t('components.workflow.trigger') }}</span>
        </template>

        <div class="rounded-xl border pb-4">
          <div class="px-4 py-2 bg-primary-100 rounded-t-xl text-sm">
            {{ $t('components.workflow.if_the_following_is_true') }}
          </div>
            <div class="flex gap-4 px-4">
              <template v-for="element in eventsElements">
                <FormulateInput
                  v-model="events.model[element.key]"
                  :key="`events-${element.key}`"
                  :label="element.label"
                  :placeholder="element.placeholder"
                  :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                  :options="getMappedObjectOptions('events', element.key)"
                  v-bind="dropdownsWIthRequiredProps"
                  @input="($event) => handleObjectInput($event, 'events', element.key)"
                />
              </template>

              <FormulateInput
                v-if="events.options.subConfiguration.length > 0"
                v-model="events.model.subConfiguration"
                :label="$t('components.workflow.sub_configuration')"
                :placeholder="$t('components.workflow.select_sub_configuration')"
                :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                :options="getMappedObjectOptions('events', 'subConfiguration')"
                v-bind="dropdownsWIthRequiredProps"
                @input="($event) => handleObjectInput($event, 'events', 'subConfiguration')"
              />

              <div
                v-if="events.model.subConfiguration && events.options.subSubConfiguration1.length > 0"
                class="flex gap-4 px-4"
              >
                <template v-for="subSubConfigKey, sscIdx in getSubSubConfigsFromItem(events.model)">
                  <FormulateInput
                    v-if="checkShowNestedSubConfig(events.model, 'events', sscIdx)"
                    :key="`events-${sscIdx}`"
                    v-model="events.model[subSubConfigKey]"
                    :label="`Sub Configuration ${sscIdx + 1}`"
                    :placeholder="`Select Sub Configuration ${sscIdx + 1}`"
                    :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                    :options="getMappedObjectOptions('events', subSubConfigKey)"
                    v-bind="dropdownsCommonProps"
                    @input="($event) => handleObjectInput($event, 'events', subSubConfigKey)"
                  />
                </template>
            </div>
          </div>
        </div>
      </UISection>

      <UISection class="mb-4">
        <template #title>
          <span class="text-white-text">{{ $t('components.workflow.condition') }}</span>
        </template>

        <template v-for="block, blockIndex in conditions.blocks">
          <div class="my-2 pb-4 rounded-xl border" :key="block._blockId"><!-- eslint-disable-line -->
            <div class="flex items-center justify-between gap-2 px-4 py-2 bg-primary-100 rounded-t-xl text-sm">
              <div class="flex gap-2">
              <ConditionOperator
                v-if="blockIndex > 0"
                isBlocks
                :operator="conditions.operator"
                :disabled="isLoading || isViewMode"
                class="mr-2"
                @select="($event) => selectOperator($event, 'conditions')"
              />
              <ConditionOperator
                :operator="block.operator"
                :disabled="isLoading || isViewMode"
                @select="($event) => selectOperator($event, 'conditions', blockIndex)"
              />
              </div>
              <SectionHeaderControl
                v-bind="headerControlsProps[blockIndex]"
                v-on="headerControlsEvents[blockIndex]"
              />
            </div>

            <template v-for="item, index in block.model">
              <div class="flex items-center gap-4 w-full px-4" :key="`${block._blockId}-${item._itemId}`">
                <div class="flex justify-center items-center w-6 h-6 mt-6 rounded-full bg-base-content-100">
                  {{ getItemCount("conditions", index, blockIndex) }}
                </div>

                <div class="grid auto-cols-1 auto-rows-auto gap-2 flex-1">
                  <div class="flex gap-4 w-full overflow-hidden">
                    <FormulateInput
                      v-model="item.type"
                      :label="$t('components.workflow.condition_type')"
                      :placeholder="$t('components.workflow.select_condition_type')"
                      :outer-class="(context, classes) => ['flex-grow workflow-trigger-formulate-wrapper'].concat(classes)"
                      :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                      :options="getMappedArrayOptions('conditions', 'type', index, blockIndex)"
                      v-bind="dropdownsCommonProps"
                      :type="getComponentType('conditions', 'type', index, blockIndex)"
                      variant="alt"
                      @input="($event) => handleArrayInput($event, index, 'conditions', 'type', blockIndex)"
                    />

                    <ConfigurationComponent
                      :componentType="block.components[index].configuration"
                      :value="item.configuration"
                      :options="getMappedArrayOptions('conditions', 'configuration', index, blockIndex)"
                      :label="$t('components.workflow.configuration')"
                      :placeholder="$t('components.workflow.select_configuration')"
                      :disabled="isLoading || isViewMode"
                      class="flex-grow workflow-trigger-formulate-wrapper"
                      @input="($event) => item.configuration = $event"
                      @handleInput="($event) => handleArrayInput($event, index, 'conditions', 'configuration', blockIndex)"
                    />

                    <FormulateInput
                      v-if="block.components[index].configuration === selectComponentType && checkShowSubConfig(item.configuration, 'conditions', 'configuration', index, blockIndex)"
                      v-model="item.subConfiguration"
                      :label="$t('components.workflow.sub_configuration')"
                      :placeholder="$t('components.workflow.select_sub_configuration')"
                      :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                      :outer-class="(context, classes) => ['flex-grow workflow-trigger-formulate-wrapper'].concat(classes)"
                      :options="getMappedArrayOptions('conditions', 'subConfiguration', index, blockIndex)"
                      v-bind="dropdownsCommonProps"
                      :type="getComponentType('conditions', 'subConfiguration', index, blockIndex)"
                      variant="alt"
                      @input="($event) => handleArrayInput($event, index, 'conditions', 'subConfiguration', blockIndex)"
                    />
                  </div>

                  <div
                    v-if="item.subConfiguration && block.options[index].subSubConfiguration1.length > 0"
                    class="flex gap-4"
                  >
                    <template v-for="subSubConfigKey, sscIdx in getSubSubConfigsFromItem(item)">
                      <FormulateInput
                        v-if="checkShowNestedSubConfig(item, 'conditions', sscIdx, index, blockIndex)"
                        :key="`${index}-${sscIdx}`"
                        v-model="item[subSubConfigKey]"
                        :label="`Sub Configuration ${sscIdx + 1}`"
                        :placeholder="`Select Sub Configuration ${sscIdx + 1}`"
                        :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                        :options="getMappedArrayOptions('conditions', subSubConfigKey, index, blockIndex)"
                        v-bind="dropdownsCommonProps"
                        :type="getComponentType('conditions', subSubConfigKey, index, blockIndex)"
                        variant="alt"
                        @input="($event) => handleArrayInput($event, index, 'conditions', subSubConfigKey, blockIndex)"
                      />
                    </template>
                  </div>
                </div>

                <TriggerControls
                  :model="block.model"
                  field="conditions"
                  :itemIndex="index"
                  :isLoading="isLoading"
                  :isViewMode="isViewMode"
                  class="mt-6"
                  @onRemoveItem="removeItem('conditions', index, blockIndex)"
                  @onAddItem="addItem('conditions', blockIndex)"
                />
              </div>
            </template>
          </div>
        </template>
      </UISection>

      <UISection class="mb-4" :scrollable="false">
        <template #title>
          <span class="text-white-text">{{ $t("components.workflow.action") }}</span>
        </template>

        <div class="rounded-xl border">
          <ActionsHeader
            :isFullLogicShown="isFullLogicShown"
            :isConditionsPartiallyFilled="isConditionsPartiallyFilled"
            :conditions="conditions"
            @click="isFullLogicShown = !isFullLogicShown"
          />

          <div
            v-for="item, index in actions.model"
            :key="`actions-${item._itemId}`"
            class="flex gap-4 px-4"
          >
            <ActionDelay
              :model="actions.delay[index]"
              :disabled="isLoading || isViewMode"
              @select="($event) => setActionDelay($event, index)"
            />

            <div class="flex gap-4 flex-1">
              <template v-for="element in actionsElements">
                <FormulateInput
                  v-model="item[element.key]"
                  :key="`actions-${element.key}`"
                  :label="element.label"
                  :placeholder="element.placeholder"
                  :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                  :options="getMappedArrayOptions('actions', element.key, index)"
                  v-bind="dropdownsWIthRequiredProps"
                  @input="($event) => handleArrayInput($event, index, 'actions', element.key)"
                />
              </template>

              <ConfigurationComponent
                v-if="checkShowSubConfig(item.configuration, 'actions', 'configuration', index)"
                :componentType="actions.components[index].subConfiguration"
                :value="item.subConfiguration"
                :options="getMappedArrayOptions('actions', 'subConfiguration', index)"
                :label="$t('components.workflow.configuration')"
                :placeholder="$t('components.workflow.select_configuration')"
                :disabled="isLoading || isViewMode"
                class="flex-grow"
                @input="($event) => item.subConfiguration = $event"
                @handleInput="($event) => handleArrayInput($event, index, 'actions', 'subConfiguration')"
              />

              <div
                v-if="item.subConfiguration && actions.options[index].subSubConfiguration1.length > 0"
                class="flex gap-4"
              >
                <template v-for="subSubConfigKey, sscIdx in getSubSubConfigsFromItem(item)">
                  <FormulateInput
                    v-if="checkShowNestedSubConfig(item, 'actions', sscIdx, index)"
                    :key="`${index}-${sscIdx}`"
                    v-model="item[subSubConfigKey]"
                    :label="`Sub Configuration ${sscIdx + 1}`"
                    :placeholder="`Select Sub Configuration ${sscIdx + 1}`"
                    :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
                    :options="getMappedArrayOptions('actions', subSubConfigKey, index)"
                    v-bind="dropdownsWIthRequiredProps"
                    @input="($event) => handleArrayInput($event, index, 'actions', subSubConfigKey)"
                  />
                </template>
              </div>
            </div>

            <TriggerControls
              :model="actions.model"
              field="actions"
              :itemIndex="index"
              :isLoading="isLoading"
              :isViewMode="isViewMode"
              @onRemoveItem="removeItem('actions', index)"
              @onAddItem="addItem('actions')"
            />
          </div>
        </div>
      </UISection>

      <div
        v-if="!isViewMode"
        class="flex justify-end mt-4 px-4 py-5 bg-white rounded-2xl h-20 shadow-md"
      >
        <Button
          type="button"
          :text="$t(getTriggerId ? 'actions.update' : 'actions.save')"
          :disabled="isLoading || disabledSubmit"
          class="bg-primary"
          @click="handleSubmit"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { clone } from "lodash";
import Button from "@shared/components/button";
import Loader from "@/components/loader";
import {
  TriggerControls,
  SectionHeaderControl,
  ConditionOperator,
  ConfigurationComponent,
  ActionDelay,
  ActionsHeader,
} from "./components";
import helperMixin from "./mixins/helper";
import itemsManagerMixin from "./mixins/itemsManager";
import dataHandlerMixin from "./mixins/dataHandler";
import {
  FIELD_KEYS,
  DEFAULT_OPERATOR_MODEL,
  DEFAULT_CONFIG_MODEL,
  DEFAULT_CONFIG_OPTIONS,
  DEFAULT_COMPONENTS_CONFIG,
  DEFAULT_DELAY_MODEL,
} from "./constants.js";

// All API interaction methods are in dataHandlerMixin
// All computed properties, utils and helper methods are in helperMixin
// All logic to interact with blocks / items is in itemsManagerMixin
export default {
  name: "CreateTriggersAndActions",

  mixins: [dataHandlerMixin, helperMixin, itemsManagerMixin],

  components: {
    Button,
    Loader,
    TriggerControls,
    SectionHeaderControl,
    ConditionOperator,
    ConfigurationComponent,
    ActionDelay,
    ActionsHeader,
  },

  data() {
    return {
      details: {
        model: { name: "", description: "" },
        errors: { name: false, description: false },
      },
      events: {
        model: clone(DEFAULT_CONFIG_MODEL),
        options: { [FIELD_KEYS.Type]: [], ...DEFAULT_CONFIG_OPTIONS },
        memo: {},
      },
      conditions: {
        blocks: [{
          model: [{ ...clone(DEFAULT_CONFIG_MODEL), _itemId: 0 }],
          options: [{ [FIELD_KEYS.Type]: [], ...DEFAULT_CONFIG_OPTIONS, }],
          components: [clone(DEFAULT_COMPONENTS_CONFIG)],
          operator: clone(DEFAULT_OPERATOR_MODEL),
          _blockId: 0,
        }],
        operator: clone(DEFAULT_OPERATOR_MODEL),
        memo: {},
        blocksCount: 0,
        itemsCount: 0,
      },
      actions: {
        model: [{ ...clone(DEFAULT_CONFIG_MODEL), _itemId: 0 }],
        options: [{ [FIELD_KEYS.Type]: [], ...DEFAULT_CONFIG_OPTIONS }],
        components: [clone(DEFAULT_COMPONENTS_CONFIG)],
        delay: [clone(DEFAULT_DELAY_MODEL)],
        memo: {},
        itemsCount: 0,
      },
      isLoading: false,
      isFullLogicShown: false,
      isLoaderShown: true,
    }
  },

  async mounted() {
    await this.fetchInitialData();
  },
}
</script>

<style lang="scss">
.workflow-trigger-formulate-wrapper {
  max-width: 50%;
}
</style>
